<template>
  <div class="product__add-actions flex items-stretch">
    <div
      v-if="availableToPurchase || (!availableToPurchase && selectedQuantityIsMoreThanInventory)"
      class="product__quantity quantity-buttons"
    >
      <button
        class="quantity-buttons__button"
        :disabled="isBundle ? bundleQuantity <= 1 : quantity <= 1"
        aria-label="Decrease quantity"
        @click="decreaseQuantity"
      >
        <MinusCircleFilled />
      </button>
      <span class="quantity-buttons__amount">
        {{ isBundle ? bundleQuantity : quantity }}
      </span>
      <button
        class="quantity-buttons__button"
        :class="{ 'quantity-buttons__button--disabled': customerHasReachedQuantityLimit }"
        :disabled="customerHasReachedQuantityLimit"
        aria-label="Increase quantity"
        @click="increaseQuantity"
      >
        <PlusCircleFilled />
      </button>
    </div>

    <button
      v-if="showBackInStockNotify"
      class="product__add-cta btn btn--black"
      @click="showBackInStockPopup"
    >
      {{ addToCartText }}
    </button>

    <button
      v-else
      class="product__add-cta btn"
      :class="{ 'btn--black': availableToPurchase, 'btn--grey': !availableToPurchase, 'product__add-cta--adding': addingProductToCart }"
      :disabled="!availableToPurchase"
      @click="addToCart"
    >
      {{ addToCartText }}
    </button>
  </div>
</template>

<script>
import { useCartStore } from '../../pinia/CartStore'
import { useProductStore } from '../../pinia/ProductStore'
import { useBundleStore } from '../../pinia/BundleStore'
import { mapState, mapActions } from 'pinia'

import PlusCircleFilled from '../../icons/plus-circle-filled'
import MinusCircleFilled from '../../icons/minus-circle-filled'

export default {
  components: {
    PlusCircleFilled,
    MinusCircleFilled
  },
  props: {
    quickView: {
      type: Boolean,
      default: false
    },
    quickViewSelectedVariant: {
      type: Object,
      default: () => {}
    },
    quickViewProduct: {
      type: Object,
      default: () => {}
    },
    quickViewPrice: {
      type: String,
      default: () => {}
    }
  },
  data () {
    return {
      quantity: 1,
      strings: { ...window.theme.strings.product },
      addingProductToCart: false,
      addingToCartText: 'Adding to cart...',
      purchaseLimitReachedText: 'Purchase limit reached'
    }
  },
  computed: {
    ...mapState (useProductStore, [
      'product',
      'selectedVariant',
      'productHasPurchaseLimit',
      'purchaseLimit',
      'selectedSellingPlan',
      'priceFormatted',
      'variantPriceFormatted'
    ]),
    ...mapState (useBundleStore, [
      'isBundle',
      'bundlePrice',
      'bundlePayload',
      'bundleQuantity',
      'bundlePayload',
      'enableAddToCart'
    ]),
    ...mapState (useCartStore, [
      'items',
    ]),

    productToAdd () {
      if (this.quickView) {
        return this.quickViewProduct
      } else {
        return this.product
      }
    },

    productToAddPrice () {
      if (this.quickView) {
        return this.quickViewPrice
      } else {
        return this.variantPriceFormatted
      }
    },

    productToCheck () {
      if (this.quickView) {
        return this.quickViewSelectedVariant.product
      } else {
        return this.productToAdd
      }
    },

    isPreOrder () {
      return this.productToCheck.tags.includes('preorder') && this.productToAdd.available
    },

    isComingSoon () {
      return this.productToCheck.tags.includes('coming-soon') && !this.productToAdd.available
    },

    selectedQuantityIsMoreThanInventoryText () {
      return `Only ${this.productToAdd.inventoryQuantity} available`
    },

    selectedQuantityIsMoreThanInventory () {
      return !this.soldOut && this.quantity > this.productToAdd.inventoryQuantity
    },

    matchingCartItem () {
      return this.items.find(item => item.handle === this.productToAdd.handle)
    },

    productQuantityAddLimit () {
      if (this.quickView) {
        return 999999
      }

      if (!this.productHasPurchaseLimit) {
        if (this.productToAdd.variants.length > 1) {
          return 999999
        } else {
          return this.productToAdd.inventoryQuantity
        }
      }

      const amountInCart = this.matchingCartItem ? this.matchingCartItem.quantity : 0
      return this.purchaseLimit - amountInCart
    },

    customerHasReachedQuantityLimit () {
      if (this.isBundle) {
        return this.bundleQuantity >= this.productQuantityAddLimit
      } else {
        return this.quantity >= this.productQuantityAddLimit
      }
    },

    customerHasExceededLimit () {
      return this.quantity > this.productQuantityAddLimit
    },
    productTags () {
      return [ ...this.productToAdd.tags ]
    },
    soldOut () {
      return this.quickView ? !this.quickViewSelectedVariant.available : !this.selectedVariant.available
    },
    availableToPurchase () {
      if (this.isBundle)  {
        return !this.soldOut && !this.customerHasExceededLimit && this.enableAddToCart
      } else {
        return !this.soldOut && !this.customerHasExceededLimit
      }
    },
    hasBackInStockNotify () {
      return !this.productTags.includes('no-bis')
    },
    showBackInStockNotify () {
      return this.soldOut && this.hasBackInStockNotify
    },
    defaultAddToCartText () {
      return this.strings.addToCart.replace('{{ price }}', (this.productToAddPrice))
    },
    addToCartText () {
      if (this.soldOut) {
        if (this.isComingSoon) {
          return this.strings.comingSoonCta
        } else if (this.hasBackInStockNotify) {
          return this.strings.backInStockNotify
        } else {
          return this.strings.soldOut
        }
      } else {
        if (this.isPreOrder) {
          return this.strings.preOrder
        } else if (this.customerHasExceededLimit) {
          if (this.selectedQuantityIsMoreThanInventory) {
            return this.selectedQuantityIsMoreThanInventoryText
          } else {
            return this.purchaseLimitReachedText
          }
        } else if (this.isBundle && !this.enableAddToCart) {
          return 'Bundle selections incomplete'
        } else {
          if (this.addingProductToCart) {
            return this.addingToCartText
          } else {
            return this.defaultAddToCartText
          }
        }
      }
    },
    payload () {
      const payload = {
        items: [
          {
            id: this.quickView ? this.quickViewSelectedVariant.id : this.selectedVariant.id,
            quantity: this.quantity
          }
        ]
      }
      if (this.selectedSellingPlan) {
        payload.items[0].selling_plan = this.selectedSellingPlan.id
      }
      return  payload
    },
    price() {
      return this.strings.addToCart.replace('{{ price }}', this.priceFormatted)
    }
  },
  mounted () {
    this.cartUpdatedListener()
  },
  methods: {
    ...mapActions(useCartStore, [
      'addItems',
      'getCart'
    ]),
    ...mapActions(useBundleStore, [
      'increaseBundleQuantity',
      'decreaseBundleQuantity',
    ]),
    ...mapActions(useProductStore, [
      'showBackInStockPopup'
    ]),

    increaseQuantity () {
      if (!this.customerHasReachedQuantityLimit) {
        if (this.isBundle) {
          this.increaseBundleQuantity()
        } else {
          this.quantity ++
        }
      }
    },
    decreaseQuantity () {
      if (this.isBundle) {
        this.decreaseBundleQuantity()
      } else {
        this.quantity > 1 && this.quantity --
      }
    },
    async addToCart () {
      if (this.availableToPurchase) {
        this.addingProductToCart = true
        if (this.isBundle) {
          await this.addItems(this.bundlePayload)
        } else {
          await this.addItems(this.payload)
        }
        this.addingProductToCart = false
      }
    },
    cartUpdatedListener () {
      document.addEventListener('cartUpdated', async () => {
        await this.getCart()
      })
    }
  }
}
</script>
